import AsyncLoader from "../../exec-utils/async-loader.js";
import EnvLightLoader from "../../output/assets/envlights/envlight-loader.js";

const Sides = ["px", "nx", "py", "ny", "pz", "nz"];

function parseEnvFile(arrayBuffer) {
    const headerView = new DataView(arrayBuffer, 0, 2);
    let offset = 2;

    if (headerView.getUint8(0) !== 0) {
        throw new Error("Invalid magic number");
    }

    const levelCount = headerView.getUint8(1);
    const data = { levels: [] };

    for (let level = 0; level < levelCount; level++) {
        const levelSides = [];

        for (let side = 0; side < Sides.length; side++) {
            const sizeView = new DataView(arrayBuffer, offset, 3);
            offset += 3;

            const s0 = sizeView.getUint8(0);
            const s1 = sizeView.getUint8(1);
            const s2 = sizeView.getUint8(2);
            const size = s0 << 16 | s1 << 8 | s2;

            const sideDataView = new DataView(arrayBuffer, offset, size);
            offset += size;

            const sideBlob = new Blob([sideDataView], { type: "image/png" });
            levelSides[side] = sideBlob;
        }

        data.levels[level] = { sides: levelSides };
    }

    return {
        levelCount,
        data,
    };
}

function makeEnvBlobUrls(envData) {
    const blobUrls = { levels: [] };

    for (const [level, { sides }] of envData.data.levels.entries()) {
        const levelBlobs = { sides: [] };
        blobUrls.levels[level] = levelBlobs;

        for (const [side, sideBlob] of sides.entries()) {
            levelBlobs.sides[side] = URL.createObjectURL(sideBlob);
        }
    }

    return blobUrls;
}

function freeEnvBlobUrls(blobUrls) {
    for (const [level, { sides }] of blobUrls.levels.entries()) {
        for (const [side, blobUrl] of sides.entries()) {
            URL.revokeObjectURL(blobUrl);
        }
    }
}

function loadCubeImage(urls) {
    const promises = urls.map(url => new Promise((resolve, reject) => {
        const image = new Image();

        image.addEventListener("load", (event) => resolve(image));
        image.addEventListener("error", (event) => reject(event.error));

        image.src = url;
    }));

    return Promise.all(promises);
}

async function loadCubeTexture(blobUrls) {
    const imagePromise = loadCubeImage(blobUrls.levels[0].sides);
    const mipmapPromises = [];
    for (let level = 1; level < blobUrls.levels.length; level++) {
        mipmapPromises[level-1] = loadCubeImage(blobUrls.levels[level].sides);
    }

    const image = await imagePromise;
    const mipmaps = await Promise.all(mipmapPromises);

    const envLight = new THREE.CubeTexture(image);
    envLight.generateMipmaps = false;
    envLight.mipmaps = mipmaps;
    envLight.premultiplyAlpha = false;
    envLight.encoding = THREE.RGBM16Encoding;
    envLight.needsUpdate = true;
    return envLight;
}

function threeLoad(threeLoader, url) {
    return new Promise((resolve, reject) => {
        threeLoader.load(url, (data) => resolve(data), undefined, (error) => reject(error));
    });
}

class LoadingFile extends AsyncLoader.Element {
    constructor(threeFileLoader, path) {
        super();

        this._threeFileLoader = threeFileLoader;
        this._path = path;

        this._promise = null;
        this._envLight = null;
        this._unload = false;
    }

    load() {
        if (this._promise) {
            throw new Error("Element is already loaded or loading");
        }

        this._promise = new Promise(async (resolve, reject) => {
            try {
                const arrayBuffer = await threeLoad(this._threeFileLoader, this._path);
                if (this._unload) resolve();

                const envData = parseEnvFile(arrayBuffer);
                const blobUrls = makeEnvBlobUrls(envData);

                let envLight;
                try {
                    envLight = await loadCubeTexture(blobUrls);
                } catch (error) {
                    freeEnvBlobUrls(blobUrls);
                    throw error;
                }

                if (this._unload) {
                    freeEnvBlobUrls(blobUrls);
                    envLight.dispose();
                    resolve();
                }

                this._blobUrls = blobUrls;
                this._envLight = envLight;
                resolve(envLight);
            } catch (error) {
                reject(error);
            }
        });

        return this._promise.catch(error => {
            console.error("Failed to load envLight: "+this._path);
        });
    }

    unload() {
        if (!this._promise) {
            throw new Error("Element is not loaded or loading");
        }

        this._unload = true;

        if (this._envLight) {
            this._envLight.dispose();
            this._envLight = null;
        }

        if (this._blobUrls) {
            freeEnvBlobUrls(this._blobUrls);
            this._blobUrls = null;
        }
    }
}

class ThreeEnvEnvLightLoader extends EnvLightLoader {
    constructor(threeFileLoader) {
        super();

        this._threeFileLoader = threeFileLoader;

        this._asyncLoader = new AsyncLoader(this._makeLoadElement.bind(this));
        this._envLightPath = new Map();
    }

    async load(key, path) {
        if (this._envLightPath.has(key)) {
            throw new Error("EnvLight is already loaded or loading");
        }

        this._envLightPath.set(key, path);
        return await this._asyncLoader.load(path);
    }

    unload(key) {
        if (!this._envLightPath.has(key)) {
            throw new Error("EnvLight is not loaded or loading");
        }

        const path = this._envLightPath.get(key);
        this._asyncLoader.unload(path);
        this._envLightPath.delete(key);
    }

    _makeLoadElement(path) {
        return new LoadingFile(this._threeFileLoader, path);
    }
}

export default ThreeEnvEnvLightLoader;
